/**
 * ProblemCard - Expandable problem card for Bag Health Check
 * 
 * Shows a specific bag problem with its impact, then expands
 * to show budget-tiered solutions when clicked.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, AlertTriangle, AlertCircle, Info, Wrench, Target, TrendingUp } from 'lucide-react';
import { BagProblem, ProblemSeverity } from '../utils/bagAnalyzer';
import { UserProfile } from '../types';

interface ClubSolution {
  tier: 'value' | 'mid' | 'premium' | 'adjustment';
  tierLabel: string;
  priceRange: string;
  clubName: string;
  clubType?: string;      // Specific club type with number (e.g., "4 Hybrid")
  intendedCarry?: string; // Target yardage range (e.g., "195-205")
  brand: string;
  model: string;
  reasoning: string;
}

interface ProblemCardProps {
  problem: BagProblem;
  userProfile: UserProfile;
  solutions?: ClubSolution[];
  onLoadSolutions?: (problem: BagProblem) => void;
  isLoadingSolutions?: boolean;
}

const severityConfig: Record<ProblemSeverity, {
  bgColor: string;
  borderColor: string;
  textColor: string;
  badgeColor: string;
  icon: React.ElementType;
  label: string;
}> = {
  critical: {
    bgColor: 'bg-red-50',
    borderColor: 'border-red-500',
    textColor: 'text-red-800',
    badgeColor: 'bg-red-100 text-red-700',
    icon: AlertTriangle,
    label: 'CRITICAL'
  },
  high: {
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-500',
    textColor: 'text-orange-800',
    badgeColor: 'bg-orange-100 text-orange-700',
    icon: AlertCircle,
    label: 'HIGH PRIORITY'
  },
  medium: {
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-500',
    textColor: 'text-yellow-800',
    badgeColor: 'bg-yellow-100 text-yellow-700',
    icon: Info,
    label: 'MEDIUM'
  },
  low: {
    bgColor: 'bg-green-50',
    borderColor: 'border-green-500',
    textColor: 'text-green-800',
    badgeColor: 'bg-green-100 text-green-700',
    icon: TrendingUp,
    label: 'OPTIMIZATION'
  }
};

const problemTypeIcons: Record<string, React.ElementType> = {
  'gapping': Target,
  'missing-coverage': Target,
  'outdated': Wrench,
  'weak-spot': AlertCircle,
  'set-optimization': TrendingUp
};

const ProblemCard: React.FC<ProblemCardProps> = ({
  problem,
  userProfile,
  solutions = [],
  onLoadSolutions,
  isLoadingSolutions = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const config = severityConfig[problem.severity];
  const SeverityIcon = config.icon;
  const TypeIcon = problemTypeIcons[problem.type] || Target;

  const handleExpand = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    
    // Load solutions when first expanded
    if (newExpanded && solutions.length === 0 && onLoadSolutions) {
      onLoadSolutions(problem);
    }
  };

  // Generate placeholder solutions based on user's budget tier if none provided
  const displaySolutions = solutions.length > 0 ? solutions : generatePlaceholderSolutions(problem, userProfile);

  return (
    <div 
      className={`rounded-xl border-l-4 ${config.borderColor} ${config.bgColor} overflow-hidden transition-all duration-300`}
    >
      {/* Header - Always visible */}
      <button
        onClick={handleExpand}
        className="w-full p-4 sm:p-5 text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Severity Badge */}
            <div className="flex items-center gap-2 mb-2">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${config.badgeColor}`}>
                <SeverityIcon className="w-3 h-3" />
                {config.label}
              </span>
              <TypeIcon className={`w-4 h-4 ${config.textColor} opacity-60`} />
            </div>
            
            {/* Title */}
            <h3 className={`font-bold text-base sm:text-lg ${config.textColor} mb-1`}>
              {problem.title}
            </h3>
            
            {/* Description */}
            <p className="text-sm text-gray-600 mb-2">
              {problem.description}
            </p>
            
            {/* Impact */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 uppercase">Impact:</span>
              <span className={`text-sm font-medium ${config.textColor}`}>
                {problem.impact}
              </span>
            </div>
          </div>
          
          {/* Expand/Collapse Arrow */}
          <div className={`flex-shrink-0 p-2 rounded-full ${config.bgColor}`}>
            {isExpanded ? (
              <ChevronUp className={`w-5 h-5 ${config.textColor}`} />
            ) : (
              <ChevronDown className={`w-5 h-5 ${config.textColor}`} />
            )}
          </div>
        </div>
        
        {/* Tap hint when collapsed */}
        {!isExpanded && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <ChevronDown className="w-3 h-3" />
              Tap to see solutions
            </span>
          </div>
        )}
      </button>

      {/* Expanded Content - Solutions */}
      {isExpanded && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-gray-200 bg-white">
          <div className="pt-4">
            <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-blue-600" />
              Solutions for Your {userProfile.equipmentTier === 'budget' ? 'Value' : userProfile.equipmentTier === 'premium' ? 'Premium' : 'Mid-Range'} Budget
            </h4>
            
            {isLoadingSolutions ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm text-gray-600">Finding best solutions...</span>
              </div>
            ) : (
              <div className="space-y-3">
                {displaySolutions.map((solution, index) => (
                  <SolutionCard key={index} solution={solution} isRecommended={index === 0} />
                ))}
              </div>
            )}
            
            {/* Hint */}
            <p className="mt-4 text-xs text-gray-500 italic">
              💡 {problem.solutionHint}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Solution Card - Individual club recommendation within expanded problem
 */
interface SolutionCardProps {
  solution: ClubSolution;
  isRecommended?: boolean;
}

const SolutionCard: React.FC<SolutionCardProps> = ({ solution, isRecommended = false }) => {
  const tierColors: Record<string, string> = {
    adjustment: 'border-amber-300 bg-amber-50',
    value: 'border-emerald-200 bg-emerald-50',
    mid: 'border-blue-200 bg-blue-50',
    premium: 'border-purple-200 bg-purple-50'
  };

  const tierBadgeColors: Record<string, string> = {
    adjustment: 'bg-amber-100 text-amber-800',
    value: 'bg-emerald-100 text-emerald-700',
    mid: 'bg-blue-100 text-blue-700',
    premium: 'bg-purple-100 text-purple-700'
  };

  const isAdjustment = solution.tier === 'adjustment';
  
  // Get display club type - prefer specific clubType over generic clubName
  const displayClubType = solution.clubType || solution.clubName;

  return (
    <div className={`rounded-lg border-2 ${tierColors[solution.tier] || tierColors.mid} p-3 relative`}>
      {isRecommended && !isAdjustment && (
        <div className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
          Best Match
        </div>
      )}
      {isAdjustment && (
        <div className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
          Try First
        </div>
      )}
      
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          {/* Top row: Tier badge and price */}
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-bold px-2 py-0.5 rounded ${tierBadgeColors[solution.tier] || tierBadgeColors.mid}`}>
              {solution.tierLabel}
            </span>
            <span className="text-xs text-gray-500">{solution.priceRange}</span>
          </div>
          
          {/* PROMINENT: Club type and intended carry - the most important info */}
          {!isAdjustment && (
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="inline-flex items-center px-2 py-1 bg-gray-800 text-white text-sm font-bold rounded">
                {displayClubType}
              </span>
              {solution.intendedCarry && solution.intendedCarry !== 'N/A' && (
                <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded">
                  📍 {solution.intendedCarry} yards
                </span>
              )}
            </div>
          )}
          
          {/* Brand and model */}
          <h5 className="font-bold text-gray-900">
            {isAdjustment ? '🔧 ' : ''}{solution.brand} {solution.model}
          </h5>
          
          {/* Reasoning */}
          <p className="text-sm text-gray-600 mt-1">
            {solution.reasoning}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Parse adjacent clubs from problem ID (format: "gap-driver-3-wood" or "gap-3-wood-3-hybrid")
 * Returns the two clubs that form the gap
 * 
 * IMPORTANT: Club names can contain hyphens (e.g., "3-wood", "3-hybrid", "7-iron")
 * so we need to use a smarter parsing approach
 */
function parseAdjacentClubs(problemId?: string): { upper: string; lower: string; upperDisplay: string; lowerDisplay: string } | null {
  if (!problemId?.startsWith('gap-')) return null;
  
  const gapContent = problemId.replace('gap-', '');
  if (!gapContent) return null;

  // Known club patterns (ordered by specificity - longest patterns first)
  const knownClubPatterns = [
    // Woods
    { pattern: 'driver', display: 'Driver', category: 'driver' },
    { pattern: '3-wood', display: '3-Wood', category: 'wood' },
    { pattern: '5-wood', display: '5-Wood', category: 'wood' },
    { pattern: '7-wood', display: '7-Wood', category: 'wood' },
    { pattern: '4-wood', display: '4-Wood', category: 'wood' },
    // Hybrids  
    { pattern: '2-hybrid', display: '2 Hybrid', category: 'hybrid' },
    { pattern: '3-hybrid', display: '3 Hybrid', category: 'hybrid' },
    { pattern: '4-hybrid', display: '4 Hybrid', category: 'hybrid' },
    { pattern: '5-hybrid', display: '5 Hybrid', category: 'hybrid' },
    { pattern: '6-hybrid', display: '6 Hybrid', category: 'hybrid' },
    // Irons
    { pattern: '3-iron', display: '3 Iron', category: 'iron' },
    { pattern: '4-iron', display: '4 Iron', category: 'iron' },
    { pattern: '5-iron', display: '5 Iron', category: 'iron' },
    { pattern: '6-iron', display: '6 Iron', category: 'iron' },
    { pattern: '7-iron', display: '7 Iron', category: 'iron' },
    { pattern: '8-iron', display: '8 Iron', category: 'iron' },
    { pattern: '9-iron', display: '9 Iron', category: 'iron' },
    // Wedges
    { pattern: 'pitching-wedge', display: 'Pitching Wedge', category: 'wedge' },
    { pattern: 'gap-wedge', display: 'Gap Wedge', category: 'wedge' },
    { pattern: 'sand-wedge', display: 'Sand Wedge', category: 'wedge' },
    { pattern: 'lob-wedge', display: 'Lob Wedge', category: 'wedge' },
    // Shorthand versions
    { pattern: 'pw', display: 'Pitching Wedge', category: 'wedge' },
    { pattern: 'gw', display: 'Gap Wedge', category: 'wedge' },
    { pattern: 'sw', display: 'Sand Wedge', category: 'wedge' },
    { pattern: 'lw', display: 'Lob Wedge', category: 'wedge' },
  ];

  // Try to find first club match starting from beginning
  let upperClub: { pattern: string; display: string; category: string } | null = null;
  let remainingContent = gapContent;

  for (const club of knownClubPatterns) {
    if (gapContent.startsWith(club.pattern)) {
      // Make sure there's a separator after the club name
      const afterClub = gapContent.substring(club.pattern.length);
      if (afterClub === '' || afterClub.startsWith('-')) {
        upperClub = club;
        remainingContent = afterClub.startsWith('-') ? afterClub.substring(1) : afterClub;
        break;
      }
    }
  }

  if (!upperClub || !remainingContent) return null;

  // Find second club from remaining content
  let lowerClub: { pattern: string; display: string; category: string } | null = null;
  for (const club of knownClubPatterns) {
    if (remainingContent === club.pattern || remainingContent.startsWith(club.pattern)) {
      lowerClub = club;
      break;
    }
  }

  if (!lowerClub) return null;

  return {
    upper: upperClub.pattern,
    lower: lowerClub.pattern,
    upperDisplay: upperClub.display,
    lowerDisplay: lowerClub.display
  };
}

/**
 * Get the numeric component of a club name for comparison
 * e.g., "3-hybrid" -> 3, "5-wood" -> 5, "driver" -> 0
 */
function getClubNumber(clubPattern: string): number {
  if (clubPattern === 'driver') return 0;
  const match = clubPattern.match(/^(\d+)/);
  return match ? parseInt(match[1]) : 99;
}

/**
 * CONTEXT-AWARE gap analysis - determines club type based on ADJACENT clubs, not just distance
 * This is critical: a gap between 3-hybrid and 5-hybrid needs a 4-hybrid, NOT a 3-wood!
 */
function getClubInfoForGap(affectedRange?: { from: number; to: number }, problemId?: string): {
  type: 'driver' | 'fairway' | 'hybrid' | 'iron' | 'wedge';
  clubName: string;
  pricing: { value: string; mid: string; premium: string };
  adjustmentSuggestion?: string; // Club-specific adjustment to try first
  adjacentClubs?: { upper: string; lower: string };
} {
  // Check for wedge-specific problems
  if (problemId?.includes('wedge')) {
    return {
      type: 'wedge',
      clubName: 'Gap Wedge',
      pricing: { value: '$50-75', mid: '$75-105', premium: '$105+' },
      adjustmentSuggestion: 'Check loft gaps on existing wedges - many sets have inconsistent spacing'
    };
  }

  // CONTEXT-AWARE: Parse adjacent clubs from problem ID
  const adjacentClubs = parseAdjacentClubs(problemId);
  
  if (adjacentClubs) {
    const { upper, lower, upperDisplay, lowerDisplay } = adjacentClubs;
    const upperNum = getClubNumber(upper);
    const lowerNum = getClubNumber(lower);
    
    // Driver to Wood/Hybrid gap: recommend fairway wood (NEVER another driver)
    if (upper === 'driver') {
      if (lower.includes('3-wood')) {
        return {
          type: 'fairway',
          clubName: 'Strong 3-Wood (13°-14°) or Mini-Driver',
          pricing: { value: '$100-180', mid: '$180-250', premium: '$250+' },
          adjustmentSuggestion: `Try lowering your ${lowerDisplay} loft to 13.5°, or adding a strong 3-wood - no need for another driver`,
          adjacentClubs
        };
      }
      return {
        type: 'fairway',
        clubName: '3-Wood or 5-Wood',
        pricing: { value: '$100-180', mid: '$180-250', premium: '$250+' },
        adjustmentSuggestion: `A fairway wood fills this gap better than any driver adjustment`,
        adjacentClubs
      };
    }
    
    // Wood to Wood gap: recommend intermediate wood
    if (upper.includes('wood') && lower.includes('wood')) {
      // Calculate the intermediate wood number
      const midNum = Math.round((upperNum + lowerNum) / 2);
      const suggestedWood = midNum <= 3 ? '3-Wood' : midNum <= 5 ? '5-Wood' : '7-Wood';
      return {
        type: 'fairway',
        clubName: suggestedWood,
        pricing: { value: '$100-180', mid: '$180-250', premium: '$250+' },
        adjustmentSuggestion: `Check loft gaps between your ${upperDisplay} and ${lowerDisplay} - consider bending or a different shaft`,
        adjacentClubs
      };
    }
    
    // Wood to Hybrid gap: recommend 7-wood or high-loft wood
    if (upper.includes('wood') && lower.includes('hybrid')) {
      return {
        type: 'fairway',
        clubName: '7-Wood or 9-Wood',
        pricing: { value: '$100-180', mid: '$180-250', premium: '$250+' },
        adjustmentSuggestion: `A 7-wood fills this gap between ${upperDisplay} and ${lowerDisplay} better than adding another hybrid`,
        adjacentClubs
      };
    }
    
    // Hybrid to Hybrid gap: recommend INTERMEDIATE hybrid (NOT a wood!)
    if (upper.includes('hybrid') && lower.includes('hybrid')) {
      // Calculate the intermediate hybrid number
      const midNum = Math.round((upperNum + lowerNum) / 2);
      const suggestedHybrid = `${midNum} Hybrid`;
      return {
        type: 'hybrid',
        clubName: suggestedHybrid,
        pricing: { value: '$100-180', mid: '$180-250', premium: '$250+' },
        adjustmentSuggestion: `Consider adjusting your ${upperDisplay} loft up or ${lowerDisplay} loft down before adding a ${suggestedHybrid}`,
        adjacentClubs
      };
    }
    
    // Hybrid to Iron gap: suggest hybrid or utility iron
    if (upper.includes('hybrid') && lower.includes('iron')) {
      const lowerIronNum = lowerNum;
      const suggestedClub = lowerIronNum <= 5 ? `${lowerIronNum - 1} Hybrid or Utility Iron` : '5 Hybrid or Utility Iron';
      return {
        type: 'hybrid',
        clubName: suggestedClub,
        pricing: { value: '$100-180', mid: '$180-250', premium: '$250+' },
        adjustmentSuggestion: `A utility iron or driving iron can fill this transition gap between ${upperDisplay} and ${lowerDisplay}`,
        adjacentClubs
      };
    }
    
    // Iron to Iron gap: suggest intermediate iron
    if (upper.includes('iron') && lower.includes('iron')) {
      const midNum = Math.round((upperNum + lowerNum) / 2);
      return {
        type: 'iron',
        clubName: `${midNum} Iron`,
        pricing: { value: '$80-120', mid: '$120-180', premium: '$180+' },
        adjustmentSuggestion: `Your iron set may need re-gapping - check lofts on ${upperDisplay} and ${lowerDisplay} with a fitter`,
        adjacentClubs
      };
    }
    
    // Wedge gaps (PW, GW, AW, SW, LW)
    const wedgeTypes = ['pw', 'gw', 'aw', 'sw', 'lw', 'pitching-wedge', 'gap-wedge', 'sand-wedge', 'lob-wedge'];
    if (wedgeTypes.some(w => upper.includes(w) || lower.includes(w)) || 
        upper.includes('wedge') || lower.includes('wedge')) {
      const midpoint = affectedRange ? (affectedRange.from + affectedRange.to) / 2 : 90;
      const loft = midpoint <= 80 ? '56°-58°' : midpoint <= 100 ? '52°-54°' : '48°-50°';
      return {
        type: 'wedge',
        clubName: `${loft} Wedge`,
        pricing: { value: '$50-75', mid: '$75-105', premium: '$105+' },
        adjustmentSuggestion: `Check your ${upperDisplay} and ${lowerDisplay} lofts - many sets have inconsistent gaps`,
        adjacentClubs
      };
    }
  }
  
  // FALLBACK: Use distance-based logic if we couldn't parse clubs from problem ID
  const midpoint = affectedRange ? (affectedRange.from + affectedRange.to) / 2 : 175;
  
  if (midpoint <= 100) {
    return {
      type: 'wedge',
      clubName: `${Math.round((midpoint / 10) * 10 + 10)}° Wedge`,
      pricing: { value: '$50-75', mid: '$75-105', premium: '$105+' },
      adjustmentSuggestion: 'Check your wedge loft spacing first'
    };
  } else if (midpoint <= 150) {
    return {
      type: 'iron',
      clubName: midpoint <= 120 ? 'Pitching Wedge' : midpoint <= 135 ? '9 Iron' : '8 Iron',
      pricing: { value: '$80-120', mid: '$120-180', premium: '$180+' },
      adjustmentSuggestion: 'Consider hybrid replacements for long irons'
    };
  } else if (midpoint <= 200) {
    return {
      type: 'hybrid',
      clubName: midpoint <= 170 ? '5 Hybrid' : midpoint <= 185 ? '4 Hybrid' : '3 Hybrid',
      pricing: { value: '$100-180', mid: '$180-250', premium: '$250+' }
    };
  } else if (midpoint <= 260) {
    // For 200-260 yards, suggest fairway wood (NOT driver)
    return {
      type: 'fairway',
      clubName: midpoint <= 230 ? '5-7 Wood' : '3 Wood',
      pricing: { value: '$100-180', mid: '$180-250', premium: '$250+' },
      adjustmentSuggestion: 'A fairway wood is the right choice here, not a driver'
    };
  } else {
    // Only for truly extreme gaps (260+ yards AND no adjacent club info)
    return {
      type: 'fairway',
      clubName: '3 Wood or Mini-Driver',
      pricing: { value: '$100-180', mid: '$180-250', premium: '$250+' },
      adjustmentSuggestion: 'A strong 3-wood or mini-driver can help fill this long-distance gap'
    };
  }
}

/**
 * Convert a ClubType from bagAnalyzer's suggestedClubType to our clubInfo format
 * This ensures we use the analyzer's recommendation (which is based on CLUB_ORDER)
 * rather than re-parsing the problem ID
 */
function getClubInfoFromSuggestedType(
  suggestedType: string,
  affectedClubs?: { type: string }[]
): {
  type: 'driver' | 'fairway' | 'hybrid' | 'iron' | 'wedge';
  clubName: string;
  pricing: { value: string; mid: string; premium: string };
  adjustmentSuggestion?: string;
} {
  // Get display names for affected clubs for adjustment suggestion
  const upperClubDisplay = affectedClubs?.[0]?.type?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'upper club';
  const lowerClubDisplay = affectedClubs?.[1]?.type?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'lower club';
  
  // Determine category from suggested type
  if (suggestedType.includes('wedge') || ['pw', 'gw', 'aw', 'sw', 'lw'].includes(suggestedType)) {
    return {
      type: 'wedge',
      clubName: suggestedType.toUpperCase().includes('W') ? suggestedType.toUpperCase() : 'Gap Wedge',
      pricing: { value: '$50-75', mid: '$75-105', premium: '$105+' },
      adjustmentSuggestion: `Check loft gaps between your ${upperClubDisplay} and ${lowerClubDisplay} first`
    };
  }
  
  if (suggestedType.includes('iron')) {
    const ironNum = suggestedType.match(/(\d)/)?.[1] || '5';
    return {
      type: 'iron',
      clubName: `${ironNum} Iron`,
      pricing: { value: '$80-120', mid: '$120-180', premium: '$180+' },
      adjustmentSuggestion: `Consider adjusting your ${upperClubDisplay} or ${lowerClubDisplay} before adding a new iron`
    };
  }
  
  if (suggestedType.includes('hybrid')) {
    const hybridNum = suggestedType.match(/(\d)/)?.[1] || '4';
    return {
      type: 'hybrid',
      clubName: `${hybridNum} Hybrid`,
      pricing: { value: '$100-180', mid: '$180-250', premium: '$250+' },
      adjustmentSuggestion: `Try adjusting your ${upperClubDisplay} loft up or ${lowerClubDisplay} loft down before adding a ${hybridNum} Hybrid`
    };
  }
  
  if (suggestedType.includes('wood')) {
    const woodNum = suggestedType.match(/(\d)/)?.[1] || '5';
    return {
      type: 'fairway',
      clubName: `${woodNum}-Wood`,
      pricing: { value: '$100-180', mid: '$180-250', premium: '$250+' },
      adjustmentSuggestion: `A ${woodNum}-wood fills the gap between your ${upperClubDisplay} and ${lowerClubDisplay}`
    };
  }
  
  // Driver (should rarely be recommended as gap filler)
  if (suggestedType === 'driver') {
    return {
      type: 'driver',
      clubName: 'Driver',
      pricing: { value: '$150-250', mid: '$250-400', premium: '$400+' },
      adjustmentSuggestion: `Check your current driver loft before adding a new one`
    };
  }
  
  // Default to hybrid
  return {
    type: 'hybrid',
    clubName: '4 Hybrid',
    pricing: { value: '$100-180', mid: '$180-250', premium: '$250+' },
    adjustmentSuggestion: `Consider adjusting your ${upperClubDisplay} or ${lowerClubDisplay} first`
  };
}

/**
 * Generate placeholder solutions based on problem type, distance, and user budget
 * Uses intelligent club type detection to recommend appropriate clubs for the gap
 * NOW INCLUDES: Club-specific adjustment suggestions as "Try First" option
 * NOW INCLUDES: Specific club type and intended carry range
 * PRIORITY: Uses problem.gapRules from bagAnalyzer when available (most accurate)
 */
function generatePlaceholderSolutions(problem: BagProblem, userProfile: UserProfile): ClubSolution[] {
  const userTier = userProfile.equipmentTier || 'medium';
  const solutions: ClubSolution[] = [];
  
  // PRIORITY: Use problem.gapRules from bagAnalyzer when available (most accurate)
  // Falls back to getClubInfoFromSuggestedType, then getClubInfoForGap
  const clubInfo = problem.suggestedClubType 
    ? getClubInfoFromSuggestedType(problem.suggestedClubType, problem.affectedClubs)
    : getClubInfoForGap(problem.affectedRange, problem.id);
  
  // Use gap rules from problem if available (more accurate)
  const specificClubName = problem.gapRules?.specificSuggestion || clubInfo.clubName;
  const adjustmentSuggestion = problem.gapRules?.adjustmentFirst || clubInfo.adjustmentSuggestion;
  
  const targetDistance = problem.affectedRange 
    ? Math.round((problem.affectedRange.from + problem.affectedRange.to) / 2)
    : 175;
  
  // Calculate intended carry range
  const intendedCarryRange = problem.affectedRange 
    ? `${problem.affectedRange.from + 5}-${problem.affectedRange.to - 5}`
    : `${targetDistance - 10}-${targetDistance + 10}`;
  
  // USER-SPECIFIED club models by category and tier
  const recommendationsByType: Record<string, { 
    value: { brand: string; model: string }; 
    mid: { brand: string; model: string }; 
    premium: { brand: string; model: string };
  }> = {
    wedge: {
      value: { brand: 'Kirkland', model: 'Signature Wedge' },
      mid: { brand: 'Cleveland', model: 'RTX ZipCore' },
      premium: { brand: 'Titleist', model: 'Vokey SM10' }
    },
    iron: {
      value: { brand: 'Takomo', model: '101T' },
      mid: { brand: 'Mizuno', model: 'JPX 923' },
      premium: { brand: 'Titleist', model: 'T-Series' }
    },
    hybrid: {
      value: { brand: 'Cobra', model: 'Air-X Hybrid' },
      mid: { brand: 'Ping', model: 'G430 Hybrid' },
      premium: { brand: 'Titleist', model: 'TSR Hybrid' }
    },
    fairway: {
      value: { brand: 'Tour Edge', model: 'Exotics' },
      mid: { brand: 'Ping', model: 'G430' },
      premium: { brand: 'TaylorMade', model: 'Qi10' }
    },
    driver: {
      value: { brand: 'Cobra', model: 'AeroJet' },
      mid: { brand: 'Callaway', model: 'Paradym X' },
      premium: { brand: 'Titleist', model: 'TSR2' }
    }
  };
  
  const typeRecs = recommendationsByType[clubInfo.type] || recommendationsByType.hybrid;
  
  if (problem.type === 'gapping' || problem.type === 'missing-coverage') {
    // ADD ADJUSTMENT SUGGESTION FIRST (if available) - This is the "Try First" option
    if (adjustmentSuggestion) {
      solutions.push({
        tier: 'adjustment',
        tierLabel: 'Try First',
        priceRange: '$0-50 (fitting/adjustment)',
        brand: 'Adjustment',
        model: 'Loft/Shaft Tweak',
        clubName: 'Existing Club Adjustment',
        clubType: 'adjustment',
        intendedCarry: intendedCarryRange,
        reasoning: adjustmentSuggestion
      });
    }
    
    // Value option
    if (userTier === 'budget' || userTier === 'medium') {
      solutions.push({
        tier: 'value',
        tierLabel: 'Value',
        priceRange: clubInfo.pricing.value,
        brand: typeRecs.value.brand,
        model: typeRecs.value.model,
        clubName: specificClubName,
        clubType: specificClubName,
        intendedCarry: intendedCarryRange,
        reasoning: `Forgiving ${specificClubName} tuned to ${intendedCarryRange} yards for consistent shots`
      });
    }
    
    // Mid option
    solutions.push({
      tier: 'mid',
      tierLabel: 'Mid-Range',
      priceRange: clubInfo.pricing.mid,
      brand: typeRecs.mid.brand,
      model: typeRecs.mid.model,
      clubName: specificClubName,
      clubType: specificClubName,
      intendedCarry: intendedCarryRange,
      reasoning: `Modern technology ${specificClubName} for precise ${intendedCarryRange} yard distance control`
    });
    
    // Premium option
    if (userTier === 'premium' || userTier === 'medium') {
      solutions.push({
        tier: 'premium',
        tierLabel: 'Premium',
        priceRange: clubInfo.pricing.premium,
        brand: typeRecs.premium.brand,
        model: typeRecs.premium.model,
        clubName: specificClubName,
        clubType: specificClubName,
        intendedCarry: intendedCarryRange,
        reasoning: `Tour-level ${specificClubName} with adjustability for precise ${intendedCarryRange} yard gapping`
      });
    }
  } else if (problem.type === 'set-optimization' && problem.id === 'wedge-gaps') {
    // Add adjustment suggestion for wedge gaps too
    solutions.push({
      tier: 'adjustment',
      tierLabel: 'Try First',
      priceRange: '$0-50 (fitting/adjustment)',
      brand: 'Adjustment',
      model: 'Loft/Shaft Tweak',
      clubName: 'Existing Wedge Check',
      clubType: 'adjustment',
      intendedCarry: '85-100',
      reasoning: 'Check actual lofts on your current wedges - manufacturing tolerances can cause unexpected gaps'
    });
    
    solutions.push({
      tier: 'value',
      tierLabel: 'Value',
      priceRange: '$50-75',
      brand: 'Cleveland',
      model: 'CBX ZipCore',
      clubName: 'Gap Wedge (50-52°)',
      clubType: 'Gap Wedge (50-52°)',
      intendedCarry: '85-100',
      reasoning: 'Forgiving cavity-back Gap Wedge for consistent 85-100 yard full shots'
    });
    
    solutions.push({
      tier: 'mid',
      tierLabel: 'Mid-Range',
      priceRange: '$75-105',
      brand: 'Callaway',
      model: 'JAWS Full Toe',
      clubName: 'Gap Wedge (50-52°)',
      clubType: 'Gap Wedge (50-52°)',
      intendedCarry: '85-100',
      reasoning: 'Aggressive grooves for maximum spin and control at 85-100 yards'
    });
    
    solutions.push({
      tier: 'premium',
      tierLabel: 'Premium',
      priceRange: '$105+',
      brand: 'Titleist',
      model: 'Vokey SM10',
      clubName: 'Gap Wedge (50-52°)',
      clubType: 'Gap Wedge (50-52°)',
      intendedCarry: '85-100',
      reasoning: 'Tour-level Gap Wedge for precise 85-100 yard control with multiple grind options'
    });
  } else {
    // Generic solutions for other problem types
    solutions.push({
      tier: 'mid',
      tierLabel: 'Mid-Range',
      priceRange: '$180-250',
      brand: 'Various',
      model: 'See Fitting',
      clubName: 'Custom Fit',
      clubType: 'Custom Fit',
      intendedCarry: 'Varies',
      reasoning: 'Complete the Equipment Fitting for personalized recommendations'
    });
  }
  
  // Sort to put adjustment first, then user's preferred tier
  // Adjustment should ALWAYS be first (if present)
  const tierOrder = userTier === 'budget' 
    ? ['adjustment', 'value', 'mid', 'premium'] 
    : userTier === 'premium' 
    ? ['adjustment', 'premium', 'mid', 'value'] 
    : ['adjustment', 'mid', 'value', 'premium'];
  
  solutions.sort((a, b) => tierOrder.indexOf(a.tier) - tierOrder.indexOf(b.tier));
  
  return solutions;
}

export default ProblemCard;
export type { ClubSolution };

